import FeatureGates, {
    FeatureGateEnvironment,
    Identifiers,
    CustomAttributes
} from '@atlassian/feature-gate-js-client';
import { get } from 'lodash';
import Logger from '../logger';

interface EnvConfig {
    apiKey: string;
    environment: FeatureGateEnvironment;
}

const TARGET_APP = 'sac-infrastructure_web';

export let statsigInitialized = false;
const getEnvironmentConfig = (): EnvConfig => {
    const environment = get(window, 'MICROS_ENVTYPE') || 'dev';
    const STATSIG_CLIENT_API_KEY = get(window, 'STATSIG_CLIENT_API_KEY');
    switch (environment) {
        case 'prod':
            return {
                apiKey: STATSIG_CLIENT_API_KEY,
                environment: FeatureGateEnvironment.Production
            };
        case 'staging':
            return {
                apiKey: STATSIG_CLIENT_API_KEY,
                environment: FeatureGateEnvironment.Staging
            };
        case 'dev':
        default:
            return {
                apiKey: STATSIG_CLIENT_API_KEY,
                environment: FeatureGateEnvironment.Development
            };
    }
};

const getAnalyticsAnonymousId = () => {
    return window?.localStorage
        ?.getItem('STATSIG_STABLE_ID')
        ?.replace(/"/g, '') as string;
};

export const initialiseStatsigClient = async (
    identifiers?: Identifiers,
    customAttributes?: CustomAttributes
): Promise<boolean> => {
    if (FeatureGates.initializeCalled() && FeatureGates.initializeCompleted()) {
        return true;
    }

    if (!identifiers) {
        identifiers = {
            analyticsAnonymousId: getAnalyticsAnonymousId()
        };
    }

    if (!customAttributes) {
        customAttributes = {
            analyticsAnonymousId: getAnalyticsAnonymousId()
        };
    }

    const envConfig: EnvConfig = getEnvironmentConfig();

    if (!envConfig.apiKey) {
        statsigInitialized = false;
        Logger.error(
            '[sac-infra] STATSIG client Initialization Error , Missing Client Key'
        );
        return false;
    }

    try {
        const options = {
            ...envConfig,
            targetApp: TARGET_APP
        };
        await FeatureGates.initialize(options, identifiers, customAttributes);
        statsigInitialized = true;
        Logger.info('[sac-infra] STATSIG client Initialized');
        return true;
    } catch (error) {
        statsigInitialized = false;
        Logger.error('[sac-infra] STATSIG client Initialization ERROR', error);
        return false;
    }
};

export const getFeatureGate = async (
    gateKey: string,
    gateDefaultValue: boolean,
    identifiers?: Identifiers,
    customAttributes?: CustomAttributes
    // eslint-disable-next-line max-params
) => {
    try {
        if (statsigInitialized) {
            if (identifiers) {
                const options = {
                    ...getEnvironmentConfig(),
                    targetApp: TARGET_APP
                };
                await FeatureGates.updateUser(
                    options,
                    identifiers,
                    customAttributes
                );
            }

            return FeatureGates.checkGate(gateKey);
        } else {
            await initialiseStatsigClient(identifiers, customAttributes);
            if (statsigInitialized) {
                return FeatureGates.checkGate(gateKey);
            } else {
                return gateDefaultValue;
            }
        }
    } catch (error) {
        Logger.error('[sac-infra] STATSIG client ERROR', error);
        return gateDefaultValue;
    }
};

export const getExperiment = async (
    experimentName: string,
    identifiers: Identifiers,
    customAttributes?: CustomAttributes
) => {
    if (statsigInitialized) {
        const options = {
            ...getEnvironmentConfig(),
            targetApp: TARGET_APP
        };

        await FeatureGates.updateUser(options, identifiers, customAttributes);
        return FeatureGates.getExperiment(experimentName);
    } else {
        await initialiseStatsigClient(identifiers, customAttributes);
        return FeatureGates.getExperiment(experimentName);
    }
};
